import { useState, useEffect, useRef } from 'react';
import { Search, Loader2, WifiOff, Store, Clock, ArrowRight, MapPin, RefreshCw, TrendingDown, X, History, Trash2, Sparkles } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { scrapeGroceryPrices, ScrapedProduct } from '@/lib/api/scraper';
import { useUserLocation } from '@/hooks/useUserLocation';
import { groupProducts, GroupedProduct } from '@/components/compare/groupProducts';
import GroupedProductCard from '@/components/compare/GroupedProductCard';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';

import bigbasketLogo from '@/assets/stores/bigbasket.png';
import blinkitLogo from '@/assets/stores/blinkit.png';
import zeptoLogo from '@/assets/stores/zepto.png';
import instamartLogo from '@/assets/stores/instamart.png';
import flipkartLogo from '@/assets/stores/flipkart.png';
import jiomartLogo from '@/assets/stores/jiomart.png';

const storeConfig: Record<string, { name: string; logo: string }> = {
  bigbasket: { name: 'BigBasket', logo: bigbasketLogo },
  blinkit: { name: 'Blinkit', logo: blinkitLogo },
  zepto: { name: 'Zepto', logo: zeptoLogo },
  instamart: { name: 'Instamart', logo: instamartLogo },
  flipkart_minutes: { name: 'Flipkart Minutes', logo: flipkartLogo },
  jiomart: { name: 'JioMart', logo: jiomartLogo },
};



// Common grocery catalog used for autocomplete suggestions while typing
const GROCERY_CATALOG = [
  'Amul milk', 'Amul butter', 'Amul cheese', 'Amul ghee', 'Amul dahi', 'Amul paneer',
  'Mother Dairy milk', 'Nestle milk',
  'Tata salt', 'Tata sampann besan', 'Tata tea gold',
  'Aashirvaad atta', 'Aashirvaad multigrain atta', 'Pillsbury atta',
  'Fortune sunflower oil', 'Fortune mustard oil', 'Fortune rice bran oil',
  'Saffola gold oil', 'Saffola active oil', 'Saffola oats',
  'Maggi noodles', 'Yippee noodles', 'Top Ramen noodles',
  'Kissan jam', 'Kissan ketchup', 'Maggi ketchup', 'Heinz ketchup',
  'Britannia bread', 'Britannia good day biscuits', 'Britannia marie gold',
  'Parle-G biscuits', 'Hide & Seek biscuits', 'Bourbon biscuits', 'Oreo biscuits',
  'Lays chips', 'Kurkure', 'Bingo chips', 'Haldiram namkeen', 'Haldiram bhujia',
  'Basmati rice', 'India Gate basmati rice', 'Daawat basmati rice', 'Sona masoori rice',
  'Toor dal', 'Moong dal', 'Chana dal', 'Urad dal', 'Masoor dal', 'Rajma',
  'Sugar', 'Jaggery', 'Eggs',
  'Onion', 'Tomato', 'Potato', 'Ginger', 'Garlic', 'Green chilli', 'Lemon',
  'Apple', 'Banana', 'Orange', 'Pomegranate', 'Grapes', 'Mango', 'Watermelon',
  'Cucumber', 'Carrot', 'Capsicum', 'Cauliflower', 'Cabbage', 'Spinach',
  'Coca Cola', 'Pepsi', 'Sprite', 'Thums Up', 'Limca', 'Mountain Dew', 'Mirinda',
  'Real juice', 'Tropicana juice', 'Frooti', 'Maaza', 'Slice',
  'Bisleri water', 'Kinley water', 'Aquafina water',
  'Red Bull', 'Sting energy drink',
  'Bournvita', 'Horlicks', 'Boost', 'Complan',
  'Nescafe coffee', 'Bru coffee', 'Tata coffee gold',
  'Lipton green tea', 'Tetley green tea', 'Red Label tea', 'Taj Mahal tea',
  'Surf Excel detergent', 'Ariel detergent', 'Tide detergent', 'Rin detergent',
  'Vim dishwash', 'Pril dishwash', 'Harpic toilet cleaner', 'Lizol floor cleaner',
  'Colgate toothpaste', 'Pepsodent toothpaste', 'Sensodyne toothpaste', 'Closeup toothpaste',
  'Dove soap', 'Lux soap', 'Lifebuoy soap', 'Pears soap', 'Santoor soap',
  'Head & Shoulders shampoo', 'Pantene shampoo', 'Dove shampoo', 'Clinic Plus shampoo',
  'Pampers diapers', 'Huggies diapers', 'MamyPoko diapers',
  'Whisper sanitary pads', 'Stayfree sanitary pads',
  'Dettol handwash', 'Lifebuoy handwash', 'Savlon handwash',
  'Dark Fantasy biscuits', 'KitKat', 'Dairy Milk', 'Munch', '5 Star', 'Gems',
];

function buildSuggestions(q: string, history: { id: string; query: string }[]) {
  const term = q.trim().toLowerCase();
  const items: { label: string; type: 'history' | 'catalog'; id?: string }[] = [];
  const seen = new Set<string>();

  for (const h of history) {
    if (!term || h.query.toLowerCase().includes(term)) {
      const k = h.query.toLowerCase();
      if (!seen.has(k)) { seen.add(k); items.push({ label: h.query, type: 'history', id: h.id }); }
    }
  }

  if (term) {
    const starts = GROCERY_CATALOG.filter(i => i.toLowerCase().startsWith(term));
    const contains = GROCERY_CATALOG.filter(i => !i.toLowerCase().startsWith(term) && i.toLowerCase().includes(term));
    for (const i of [...starts, ...contains]) {
      const k = i.toLowerCase();
      if (!seen.has(k)) { seen.add(k); items.push({ label: i, type: 'catalog' }); }
    }
  }

  return items.slice(0, 8);
}

function loadSession<T>(key: string, fallback: T): T {
  try {
    const val = sessionStorage.getItem(key);
    return val ? JSON.parse(val) : fallback;
  } catch {
    return fallback;
  }
}

// Fire a browser notification when a search finishes. Only shown when the tab
// is hidden/unfocused so it doesn't double up with the in-app toast.
// Also flashes the document title as a fallback when notifications are blocked
// or unsupported (e.g. iOS Safari without PWA install).
let titleFlashTimer: ReturnType<typeof setInterval> | null = null;
function flashTitle(message: string) {
  if (typeof document === 'undefined') return;
  const original = document.title;
  if (titleFlashTimer) { clearInterval(titleFlashTimer); titleFlashTimer = null; }
  let toggle = false;
  titleFlashTimer = setInterval(() => {
    document.title = toggle ? original : message;
    toggle = !toggle;
  }, 1000);
  const stop = () => {
    if (titleFlashTimer) { clearInterval(titleFlashTimer); titleFlashTimer = null; }
    document.title = original;
    document.removeEventListener('visibilitychange', onVis);
    window.removeEventListener('focus', stop);
  };
  const onVis = () => { if (document.visibilityState === 'visible') stop(); };
  document.addEventListener('visibilitychange', onVis);
  window.addEventListener('focus', stop);
}

function notifySearchComplete(term: string, count: number) {
  // Skip everything if the tab is already focused — the in-app toast covers it.
  if (typeof document !== 'undefined' && document.visibilityState === 'visible' && document.hasFocus()) return;

  const title = count > 0 ? `Found ${count} results for "${term}"` : `No results for "${term}"`;
  flashTitle(`✅ ${title}`);

  try {
    if (!('Notification' in window)) return;
    if (Notification.permission !== 'granted') return;
    const n = new Notification(title, {
      body: count > 0 ? 'Tap to view live price comparisons.' : 'Try a different search term.',
      icon: '/favicon.ico',
      tag: 'grozo-search',
    });
    n.onclick = () => { window.focus(); n.close(); };
  } catch {
    /* ignore */
  }
}

export default function LiveScraper() {
  const [query, setQuery] = useState(() => loadSession('gc-search-query', ''));
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<ScrapedProduct[]>(() => loadSession('gc-search-results', []));
  const [lastQuery, setLastQuery] = useState(() => loadSession('gc-search-last', ''));
  const [activeStoreFilter, setActiveStoreFilter] = useState<string>('all');
  const [history, setHistory] = useState<{ id: string; query: string }[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const searchWrapperRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();
  const { user } = useAuth();
  const { location, loading: locationLoading, error: locationError, availableStores, detectLocation } = useUserLocation();

  // Ask for notification permission once, up front, so the prompt doesn't pop
  // up mid-search (which is what causes it to get dismissed when users switch
  // tabs). Best-effort: ignored if denied or unsupported.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!('Notification' in window)) return;
    if (Notification.permission !== 'default') return;
    const ask = () => {
      try { Notification.requestPermission().catch(() => {}); } catch { /* ignore */ }
    };
    // Defer slightly so we don't block first paint.
    const t = setTimeout(ask, 1500);
    return () => clearTimeout(t);
  }, []);

  // Persist search state to sessionStorage
  useEffect(() => { sessionStorage.setItem('gc-search-results', JSON.stringify(results)); }, [results]);
  useEffect(() => { sessionStorage.setItem('gc-search-last', lastQuery); }, [lastQuery]);
  useEffect(() => { sessionStorage.setItem('gc-search-query', JSON.stringify(query)); }, [query]);

  // Reset compare page state on auth events: SIGNED_OUT (logout) and SIGNED_IN (fresh login).
  // Cached prices (price_cache table) and search history (search_history table) are preserved
  // server-side; we only clear in-memory + sessionStorage state for the current session.
  useEffect(() => {
    const resetCompareState = () => {
      setQuery('');
      setResults([]);
      setLastQuery('');
      setActiveStoreFilter('all');
      setShowHistory(false);
      setLoading(false);
      sessionStorage.removeItem('gc-search-results');
      sessionStorage.removeItem('gc-search-last');
      sessionStorage.removeItem('gc-search-query');
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT' || event === 'SIGNED_IN') {
        resetCompareState();
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Reset highlighted suggestion when query changes or dropdown closes
  useEffect(() => { setHighlightedIndex(-1); }, [query, showHistory]);

  // Load search history for the signed-in user
  const loadHistory = async () => {
    if (!user) { setHistory([]); return; }
    const { data } = await supabase
      .from('search_history')
      .select('id, query, searched_at')
      .eq('user_id', user.id)
      .order('searched_at', { ascending: false })
      .limit(50);
    if (data) {
      // De-duplicate by query (keep most recent)
      const seen = new Set<string>();
      const unique: { id: string; query: string }[] = [];
      for (const row of data) {
        const key = row.query.toLowerCase();
        if (!seen.has(key)) {
          seen.add(key);
          unique.push({ id: row.id, query: row.query });
        }
      }
      setHistory(unique.slice(0, 10));
    }
  };

  useEffect(() => {
    loadHistory();
  }, [user]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (searchWrapperRef.current && !searchWrapperRef.current.contains(e.target as Node)) {
        setShowHistory(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const saveToHistory = async (q: string) => {
    if (!user || !q.trim()) return;
    await supabase.from('search_history').insert({ user_id: user.id, query: q.trim() });
    loadHistory();
  };

  const removeHistoryItem = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    await supabase.from('search_history').delete().eq('id', id);
    setHistory(prev => prev.filter(h => h.id !== id));
  };

  const clearAllHistory = async () => {
    if (!user) return;
    await supabase.from('search_history').delete().eq('user_id', user.id);
    setHistory([]);
  };

  const pickHistory = (q: string) => {
    setQuery(q);
    setShowHistory(false);
    // Trigger search with the picked query
    setTimeout(() => {
      const form = searchWrapperRef.current?.querySelector('form');
      form?.requestSubmit();
    }, 0);
  };

  const handleSearch = async () => {
    if (!query.trim()) return;
    setLoading(true);
    setResults([]);
    setActiveStoreFilter('all');
    setShowHistory(false);

    const searchedTerm = query.trim();

    try {
      const response = await scrapeGroceryPrices(searchedTerm, availableStores);

      if (response.success && response.data) {
        setResults(response.data);
        setLastQuery(searchedTerm);
        saveToHistory(searchedTerm);
        if (response.data.length === 0) {
          toast({ title: 'No results', description: 'No products found. Try a different search term.' });
        } else {
          toast({ title: `Found ${response.data.length} products`, description: `Prices fetched for "${query}"` });
        }
        notifySearchComplete(searchedTerm, response.data.length);
      } else {
        toast({ title: 'Search failed', description: response.error || 'Could not fetch prices.', variant: 'destructive' });
      }
    } catch {
      toast({ title: 'Error', description: 'Failed to connect to the price service.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setResults([]);
    setLastQuery('');
    setQuery('');
    setActiveStoreFilter('all');
    sessionStorage.removeItem('gc-search-results');
    sessionStorage.removeItem('gc-search-last');
    sessionStorage.removeItem('gc-search-query');
  };

  const storesInResults = [...new Set(results.map(r => r.store))];

  const filteredResults = activeStoreFilter === 'all'
    ? results
    : results.filter(r => r.store === activeStoreFilter);

  const grouped = groupProducts(filteredResults);
  const lowestPrice = results.length > 0 ? Math.min(...results.map(r => r.price)) : 0;

  const suggestions = ['Amul milk', 'Tata salt', 'Aashirvaad atta', 'Maggi noodles', 'Fortune oil'];

  return (
    <div className="space-y-8">
      {/* Search section */}

      {/* Available stores pills */}
      {!loading && !lastQuery && (
        <div className="flex justify-center gap-2 flex-wrap animate-fade-in">
          {availableStores.map(storeId => {
            const cfg = storeConfig[storeId];
            if (!cfg) return null;
            return (
              <div key={storeId} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-card border border-border/40 text-xs font-medium text-muted-foreground">
                <img src={cfg.logo} alt={`${cfg.name} store logo`} className="h-3.5 w-3.5 object-contain rounded-sm" />
                {cfg.name}
              </div>
            );
          })}
        </div>
      )}

      {/* Hero search */}
      <div className="relative text-center">
        <div className="absolute inset-0 -z-10 overflow-hidden">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-[radial-gradient(ellipse_at_center,hsl(262_83%_58%/0.08)_0%,transparent_70%)]" />
        </div>

        <h1 className="font-display text-3xl sm:text-4xl md:text-5xl font-extrabold text-foreground tracking-tight mb-3">
          Compare <span className="gradient-text">Grocery Prices</span>
        </h1>
        <p className="text-muted-foreground text-base md:text-lg max-w-lg mx-auto mb-8 font-medium">
          {location
            ? `Real-time prices from ${availableStores.length} stores in ${location.city}`
            : 'Search any item and get real-time prices from 5+ stores instantly'}
        </p>

        <div ref={searchWrapperRef} className="mx-auto max-w-2xl relative">
        <form
          onSubmit={e => { e.preventDefault(); handleSearch(); }}
        >
          <div className="relative flex items-center gap-0 rounded-2xl border-2 border-border/60 bg-card shadow-lg focus-within:border-primary/50 focus-within:shadow-xl focus-within:shadow-primary/5 transition-all duration-300 overflow-hidden">
            <Search className="absolute left-5 h-5 w-5 text-muted-foreground pointer-events-none" />
            <Input
              value={query}
              onChange={e => setQuery(e.target.value)}
              onFocus={() => setShowHistory(true)}
              onKeyDown={(e) => {
                if (!showHistory) return;
                const items = buildSuggestions(query, history);
                if (items.length === 0) return;
                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  setHighlightedIndex(i => (i + 1) % items.length);
                } else if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  setHighlightedIndex(i => (i <= 0 ? items.length - 1 : i - 1));
                } else if (e.key === 'Enter') {
                  if (highlightedIndex >= 0 && highlightedIndex < items.length) {
                    e.preventDefault();
                    pickHistory(items[highlightedIndex].label);
                  }
                } else if (e.key === 'Escape') {
                  setShowHistory(false);
                }
              }}
              placeholder="Search for milk, rice, oil, snacks..."
              className="h-14 md:h-16 pl-14 pr-4 text-base md:text-lg border-0 bg-transparent shadow-none focus-visible:ring-0 font-medium placeholder:text-muted-foreground/60"
              disabled={loading}
            />
            <Button
              type="submit"
              disabled={loading || !query.trim()}
              className="h-10 md:h-12 mr-2 px-5 md:px-7 rounded-xl font-bold text-sm md:text-base shadow-md hover:shadow-lg transition-all duration-300 shrink-0"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Search <ArrowRight className="h-4 w-4 ml-1" /></>}
            </Button>
          </div>
        </form>

          {/* Search history dropdown */}
          {showHistory && (() => {
            const items = buildSuggestions(query, history);
            if (items.length === 0) return null;
            const hasHistoryItems = items.some(i => i.type === 'history');
            const heading = query.trim()
              ? 'Suggestions'
              : 'Recent searches';
            const HeadingIcon = query.trim() ? Sparkles : History;
            return (
              <div className="absolute left-0 right-0 top-full mt-2 z-30 rounded-2xl border-2 border-border/60 bg-card shadow-xl overflow-hidden text-left animate-fade-in">
                <div className="flex items-center justify-between px-4 py-2.5 border-b border-border/40 bg-muted/30">
                  <span className="text-xs font-semibold text-muted-foreground flex items-center gap-1.5">
                    <HeadingIcon className="h-3.5 w-3.5" /> {heading}
                  </span>
                  {user && hasHistoryItems && !query.trim() && (
                    <button
                      onClick={clearAllHistory}
                      className="text-xs font-medium text-destructive hover:underline"
                    >
                      Clear all
                    </button>
                  )}
                </div>
                <ul className="max-h-72 overflow-y-auto">
                  {items.map((item, idx) => (
                    <li
                      key={`${item.type}-${item.id ?? item.label}-${idx}`}
                      onMouseDown={(e) => { e.preventDefault(); pickHistory(item.label); }}
                      onMouseEnter={() => setHighlightedIndex(idx)}
                      className={`flex items-center justify-between gap-2 px-4 py-2.5 cursor-pointer group ${
                        highlightedIndex === idx ? 'bg-accent/60' : 'hover:bg-accent/60'
                      }`}
                    >
                      <span className="flex items-center gap-2.5 text-sm text-foreground truncate">
                        {item.type === 'history' ? (
                          <Clock className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                        ) : (
                          <Search className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                        )}
                        {item.label}
                        {item.type === 'history' && (
                          <span className="text-[10px] uppercase tracking-wide text-muted-foreground/70 font-medium ml-1">recent</span>
                        )}
                      </span>
                      {item.type === 'history' && item.id && (
                        <button
                          onClick={(e) => removeHistoryItem(item.id!, e)}
                          className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-opacity"
                          aria-label="Remove from history"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })()}
        </div>

        {!lastQuery && !loading && (
          <div className="mt-5 flex flex-wrap justify-center gap-2 animate-slide-up stagger-2">
            <span className="text-xs text-muted-foreground mr-1 self-center">Try:</span>
            {suggestions.map(s => (
              <button
                key={s}
                onClick={() => setQuery(s)}
                className="px-4 py-1.5 rounded-full text-xs font-semibold bg-accent/60 dark:bg-accent text-accent-foreground border border-border/40 hover:bg-primary hover:text-primary-foreground hover:border-primary transition-all duration-200"
              >
                {s}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Loading */}
      {loading && (
        <div className="text-center py-16 animate-fade-in">
          <div className="relative inline-block mb-5">
            <div className="w-18 h-18 rounded-2xl bg-primary/10 dark:bg-primary/20 flex items-center justify-center p-5" style={{ animation: 'pulse-glow 2s infinite' }}>
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          </div>
          <p className="font-display text-lg font-bold text-foreground mb-1">Fetching live prices...</p>
          <p className="text-sm text-muted-foreground">
            Checking {availableStores.map(s => storeConfig[s]?.name || s).join(', ')}
          </p>
          {location && (
            <div className="flex items-center justify-center gap-1.5 mt-2 text-xs text-primary font-medium">
              <MapPin className="h-3 w-3" />
              <span>Searching stores in {location.city}</span>
            </div>
          )}
          <div className="flex items-center justify-center gap-1.5 mt-3 text-xs text-muted-foreground">
            <Clock className="h-3 w-3" /><span>Usually takes 15-30 seconds</span>
          </div>
          <div className="mx-auto mt-5 w-64 h-1.5 rounded-full overflow-hidden bg-muted">
            <div className="h-full w-full shimmer rounded-full" />
          </div>
        </div>
      )}

      {/* Results */}
      {!loading && results.length > 0 && (
        <div className="space-y-6 animate-fade-in">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-1">
            <div className="flex-1">
              <h2 className="font-display text-xl md:text-2xl font-bold text-foreground">
                {grouped.length} results for "<span className="text-primary">{lastQuery}</span>"
              </h2>
              <div className="flex items-center gap-3 mt-1">
                <p className="text-sm text-muted-foreground flex items-center gap-1.5">
                  <TrendingDown className="h-3.5 w-3.5 text-primary" />
                  Lowest price: <span className="font-bold text-primary">₹{lowestPrice}</span>
                </p>
                {location && (
                  <p className="text-sm text-muted-foreground flex items-center gap-1.5">
                    <MapPin className="h-3.5 w-3.5 text-primary" />
                    {location.city}
                  </p>
                )}
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={handleClear} className="gap-1.5 rounded-xl text-destructive hover:text-destructive">
              <X className="h-3.5 w-3.5" /> Clear
            </Button>
          </div>

          {/* Store filter chips with logos */}
          <div className="flex gap-2.5 overflow-x-auto pb-1 scrollbar-hide">
            <button
              onClick={() => setActiveStoreFilter('all')}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-2xl text-xs font-bold transition-all whitespace-nowrap border-2 ${
                activeStoreFilter === 'all'
                  ? 'bg-primary text-primary-foreground border-primary shadow-lg shadow-primary/20'
                  : 'bg-card border-border text-muted-foreground hover:border-primary/40 hover:text-foreground'
              }`}
            >
              <Store className="h-3.5 w-3.5" />
              All Stores ({results.length})
            </button>
            {storesInResults.map(store => {
              const cfg = storeConfig[store] || { name: store, logo: '' };
              const count = results.filter(r => r.store === store).length;
              return (
                <button
                  key={store}
                  onClick={() => setActiveStoreFilter(store)}
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-2xl text-xs font-bold transition-all whitespace-nowrap border-2 ${
                    activeStoreFilter === store
                      ? 'bg-primary text-primary-foreground border-primary shadow-lg shadow-primary/20'
                      : 'bg-card border-border text-muted-foreground hover:border-primary/40 hover:text-foreground'
                  }`}
                >
                  {cfg.logo && <img src={cfg.logo} alt={`${cfg.name} store logo`} className="h-4 w-4 object-contain rounded-sm" />}
                  {cfg.name} ({count})
                </button>
              );
            })}
          </div>

          {/* Grouped product grid */}
          <div className="grid gap-5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
            {grouped.map((group, idx) => (
              <GroupedProductCard key={group.key} group={group} query={lastQuery} index={idx} />
            ))}
          </div>
        </div>
      )}

      {/* Empty state - no results */}
      {!loading && results.length === 0 && lastQuery && (
        <div className="text-center py-16 animate-fade-in">
          <div className="w-18 h-18 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-5">
            <WifiOff className="h-8 w-8 text-muted-foreground" />
          </div>
          <p className="font-display text-lg font-bold text-foreground mb-2">No results found</p>
          <p className="text-sm text-muted-foreground">
            Try searching for "<span className="text-foreground font-medium">{lastQuery}</span>" with a different spelling
          </p>
        </div>
      )}

      {/* Initial empty state */}
      {!loading && results.length === 0 && !lastQuery && (
        <div className="text-center py-12 animate-fade-in">
          <div className="w-20 h-20 rounded-3xl bg-accent/50 dark:bg-accent flex items-center justify-center mx-auto mb-5" style={{ animation: 'float 3s ease-in-out infinite' }}>
            <Search className="h-9 w-9 text-primary" />
          </div>
          <p className="font-display text-xl font-bold text-foreground mb-2">Search any grocery item</p>
          <p className="text-sm text-muted-foreground max-w-sm mx-auto">
            {location
              ? `Comparing prices from ${availableStores.length} stores available in ${location.city}`
              : 'Get real-time prices from BigBasket, Blinkit, Zepto, Instamart & Flipkart Minutes — all in one place'}
          </p>
        </div>
      )}
    </div>
  );
}
